import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import { FiTrash2 } from 'react-icons/fi';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FormTaxonomy } from '@/components/custom/form';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';

const VideoQuestionModal = props => {
  const { t } = useTranslation();
  const { onClose, formikObject, activeSelectedQuestion, quizIndex, lessonType, pageCount } = props;
  const isSlides = lessonType === 'slides';
  const [isOpenModalTaxonomies, setIsOpenModalTaxonomies] = useState(false);
  const [firstLevelTaxonomies, setFirstLevelTaxonomies] = useState([]);
  const [questionType, setQuestionType] = useState('multipla-escolha');
  const [isOpened, setIsOpened] = useState(false);
  const [isLoadingTaxonomies, setIsLoadingTaxonomies] = useState(false);

  const client = useClient();

  useEffect(() => {
    if (activeSelectedQuestion?.quiz_item_alternatives?.length <= 2) {
      setQuestionType('certo ou errado');
    } else {
      setQuestionType('multipla-escolha');
    }
  }, []);

  const createVideoQuestions = async () => {
    const items = formikObject.values.quizItemsAttributes;
    if (quizIndex !== undefined) {
      formikObject.setFieldValue(
        'quizItemsAttributes',
        items.map((item, index) => (index === quizIndex ? createVideoQuestionsForm.values : item))
      );
    } else {
      formikObject.setFieldValue('quizItemsAttributes', [...items, createVideoQuestionsForm.values]);
    }

    onClose();
  };

  const maxSlidePosition = Math.max((pageCount || 0) - 1, 0);

  const validationSchema = Yup.object({
    duration: isSlides ? Yup.string().nullable() : Yup.string().required(t('warning.requiredField')),
    slide_position: isSlides ? Yup.number().integer().min(1).max(maxSlidePosition).required(t('warning.requiredField')) : Yup.number().nullable(),
    body: Yup.string().required(t('warning.requiredField')),
    quiz_item_alternatives: Yup.array(
      Yup.object({
        title: Yup.string().required(t('warning.requiredField'))
      })
    ).required(t('warning.requiredField'))
  });

  const createVideoQuestionsForm = useFormik({
    initialValues: {
      id: activeSelectedQuestion ? activeSelectedQuestion.id : null,
      duration: activeSelectedQuestion ? activeSelectedQuestion.duration : isSlides ? null : '00:00:00',
      slide_position: activeSelectedQuestion ? activeSelectedQuestion.slide_position : isSlides ? (maxSlidePosition >= 1 ? 1 : '') : null,
      body: activeSelectedQuestion ? activeSelectedQuestion.body : '',
      selectedTaxonomies: activeSelectedQuestion ? activeSelectedQuestion.selectedTaxonomies || activeSelectedQuestion.taxonomies : [],
      quiz_item_alternatives: activeSelectedQuestion
        ? activeSelectedQuestion?.quiz_item_alternatives
        : [
            {
              'is-correct': true,
              title: ''
            },
            {
              'is-correct': false,
              title: ''
            },
            {
              'is-correct': false,
              title: ''
            },
            {
              'is-correct': false,
              title: ''
            }
          ]
    },
    validationSchema,
    onSubmit: createVideoQuestions
  });

  useEffect(() => {
    if (questionType === 'multipla-escolha') {
      createVideoQuestionsForm.setValues({
        ...createVideoQuestionsForm.values,
        quiz_item_alternatives: activeSelectedQuestion
          ? activeSelectedQuestion?.quiz_item_alternatives
          : [
              {
                'is-correct': true,
                title: ''
              },
              {
                'is-correct': false,
                title: ''
              },
              {
                'is-correct': false,
                title: ''
              },
              {
                'is-correct': false,
                title: ''
              }
            ]
      });
    } else if (questionType === 'certo ou errado') {
      createVideoQuestionsForm.setValues({
        ...createVideoQuestionsForm.values,
        quiz_item_alternatives: [
          {
            'is-correct': true,
            title: 'Certo'
          },
          {
            'is-correct': false,
            title: 'Errado'
          }
        ]
      });
    }
  }, [questionType]);

  const fetchTaxonomies = async () => {
    setIsLoadingTaxonomies(true);

    try {
      const { data } = await client.fetch('taxonomies');
      setFirstLevelTaxonomies(data);
    } catch {
      toast.error(t('toast.errorGetFirstLvlTaxonomy'));
    } finally {
      setIsLoadingTaxonomies(false);
    }
  };

  useEffect(() => {
    fetchTaxonomies();
  }, []);

  const btnGroup = [
    { id: 1, name: 'multipla-escolha', label: 'Múltipla escolha' },
    {
      id: 2,
      name: 'certo ou errado',
      label: 'Certo ou errado'
    }
  ];

  const handleChangeTrueOrFalse = index => event => {
    createVideoQuestionsForm.setValues({
      ...createVideoQuestionsForm.values,
      quiz_item_alternatives: createVideoQuestionsForm.values?.quiz_item_alternatives.map((item, i) => {
        return {
          ...item,
          'is-correct': index === i
        };
      })
    });
    createVideoQuestionsForm.handleChange(event);
  };

  useEffect(() => {
    setTimeout(() => {
      setIsOpened(true);
    }, 100);
  }, []);

  const addAlternative = () => {
    createVideoQuestionsForm?.setFieldValue('quiz_item_alternatives', [
      ...createVideoQuestionsForm?.values?.quiz_item_alternatives,
      {
        'is-correct': false,
        title: ''
      }
    ]);
  };

  return (
    <Sheet
      open={isOpened}
      onOpenChange={onClose}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="sr-only">Vídeo</SheetTitle>
        </SheetHeader>

        <SheetBody>
          <form onSubmit={createVideoQuestionsForm.handleSubmit}>
            <div className="form_row">
              {isSlides ? (
                <fieldset className="form__row">
                  <label
                    className="form__label"
                    htmlFor="slide_position"
                  >
                    {t('lessons.setSlideQuestionTitle')}
                  </label>
                  <select
                    className="form__control"
                    name="slide_position"
                    id="slide_position"
                    value={createVideoQuestionsForm.values.slide_position || 1}
                    onChange={e => createVideoQuestionsForm.setFieldValue('slide_position', Number(e.target.value))}
                  >
                    {Array.from({ length: maxSlidePosition }, (_, index) => index + 1).map(position => (
                      <option
                        key={position}
                        value={position}
                      >
                        {t('lessons.slideQuestionLabel', { position })}
                      </option>
                    ))}
                  </select>
                  <span style={{ color: 'red' }}>{createVideoQuestionsForm.touched.slide_position && createVideoQuestionsForm.errors.slide_position}</span>
                </fieldset>
              ) : (
                <fieldset className="form__row">
                  <label className="form__label">{t('lessons.setTimeQuestionTitle')}</label>
                  <input
                    onChange={createVideoQuestionsForm?.handleChange}
                    name="duration"
                    className="form__control"
                    type="text"
                    id="duration"
                    step="1"
                    value={createVideoQuestionsForm.values.duration}
                  />
                  <span style={{ color: 'red' }}>{createVideoQuestionsForm.touched.duration && createVideoQuestionsForm.errors.duration}</span>
                </fieldset>
              )}
            </div>
            <div className="form_row mb-3">
              <label className="form__label">*{t('exams.formTitleStatementQuestion')}</label>
              <Textarea
                onChange={createVideoQuestionsForm?.handleChange}
                placeholder="Escreva o enunciado aqui"
                type="text"
                id="body"
                name="body"
                value={createVideoQuestionsForm?.values.body}
              />
              <span style={{ color: 'red' }}>{createVideoQuestionsForm.touched.body && createVideoQuestionsForm.errors.body}</span>
            </div>

            <FormTaxonomy
              label={t('tasks.mattersRelated')}
              isLoading={isLoadingTaxonomies}
              taxonomies={firstLevelTaxonomies}
              selectedTaxonomies={createVideoQuestionsForm?.values?.selectedTaxonomies}
              setFieldValue={createVideoQuestionsForm?.setFieldValue}
              errors={createVideoQuestionsForm?.errors}
              touched={createVideoQuestionsForm?.touched}
              isOpenModalTaxonomies={isOpenModalTaxonomies}
              setIsOpenModalTaxonomies={setIsOpenModalTaxonomies}
            />

            <div
              className="btn-group mb-4 mt-6"
              role="group"
              aria-label="Escolha o tipo de questão"
            >
              {btnGroup?.map(btn => (
                <React.Fragment key={btn.id}>
                  <input
                    type="radio"
                    name={btn.name}
                    id={btn.id}
                    autoComplete="off"
                    checked={questionType === btn.name}
                    onChange={() => {
                      setQuestionType(btn.name);
                      createVideoQuestionsForm.setTouched({}, false);
                    }}
                  />
                  <label
                    htmlFor={btn.id}
                    className="btn btn--outline"
                  >
                    {btn.label}
                  </label>
                </React.Fragment>
              ))}
            </div>

            {questionType === 'multipla-escolha' ? (
              <>
                <div className="form-row">
                  <label className="form__label">*Alternativas</label>
                  <p className="new-questions__subtitle">Escreva o texto das alternativas e marque a correta</p>
                </div>

                {(createVideoQuestionsForm?.values?.quiz_item_alternatives || []).map((option, index) => {
                  const changeCorrectOption = () => {
                    createVideoQuestionsForm?.values?.quiz_item_alternatives.forEach((_, i) => {
                      createVideoQuestionsForm?.setFieldValue(`quiz_item_alternatives.${i}.is-correct`, false);
                    });
                    createVideoQuestionsForm?.setFieldValue(`quiz_item_alternatives.${index}.is-correct`, true);
                  };

                  const removeAlternative = () => {
                    const alternatives = createVideoQuestionsForm.values?.quiz_item_alternatives;
                    alternatives.splice(index, 1);
                    createVideoQuestionsForm.setFieldValue('quiz_item_alternatives', alternatives);
                  };

                  return (
                    <div className="form__check form__check--full">
                      <div
                        key={index}
                        style={{ display: 'flex' }}
                      >
                        <p className="mb-4">{option.option}</p>
                        <input
                          type="radio"
                          name={`quiz_item_alternatives.${index}.is-correct`}
                          checked={
                            index === 0
                              ? createVideoQuestionsForm?.values?.quiz_item_alternatives[index]?.['is-correct'] === true || createVideoQuestionsForm.values['quiz_item_alternatives'].filter(opt => opt['is-correct'] === true).length === 0
                              : createVideoQuestionsForm?.values?.quiz_item_alternatives[index]?.['is-correct'] === true
                          }
                          onChange={changeCorrectOption}
                          className="form__check-input form__check-input--rounded"
                        />
                        <input
                          name={`quiz_item_alternatives.${index}.title`}
                          value={option.title}
                          id="title"
                          type="text"
                          onChange={createVideoQuestionsForm?.handleChange}
                          className="form__control form__control--small"
                        />
                        <FiTrash2 onClick={removeAlternative} />
                      </div>
                      <span style={{ color: 'red' }}>{createVideoQuestionsForm.touched['quiz_item_alternatives']?.[index] && createVideoQuestionsForm.errors['quiz_item_alternatives']?.[index]?.title}</span>
                    </div>
                  );
                })}

                <button
                  type="button"
                  className="btn btn--small btn--full btn--outline-dashed"
                  onClick={addAlternative}
                >
                  + Alternativa
                </button>
              </>
            ) : null}

            {questionType === 'certo ou errado' && (
              <div className="form__row">
                <label className="form__label">*Marque a opção correta para a questão</label>
                <div style={{ display: 'flex', gap: 20 }}>
                  <div className="form__check">
                    <input
                      name="teste"
                      onChange={handleChangeTrueOrFalse(0)}
                      className="form__check-input"
                      type="radio"
                      checked={createVideoQuestionsForm?.values?.['quiz_item_alternatives']?.[0]?.['is-correct']}
                    />
                    <p className="new-questions__subtitle">Certo</p>
                  </div>
                  <div className="form__check">
                    <input
                      name="teste"
                      onChange={handleChangeTrueOrFalse(1)}
                      className="form__check-input"
                      type="radio"
                    />
                    <p className="new-questions__subtitle">Errado</p>
                  </div>
                </div>
              </div>
            )}
            <div className="form__row">
              <button
                className="btn btn--primary btn--wide"
                type="submit"
              >
                {t('nps.buttonAddQuestions')}
              </button>
            </div>
          </form>
        </SheetBody>

        <SheetFooter />
      </SheetContent>
    </Sheet>
  );
};

export default VideoQuestionModal;
